import React, { useState } from "react";
import { useSiteContent } from "../../services/content";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Chip from "@mui/material/Chip";
import OutlinedInput from "@mui/material/OutlinedInput";
import { motion } from "framer-motion";

const interestOptions = [
    "FIG Finance",
    "Hi5 / POS",
    "FIG KIDS (CACFP)",
    "FIG K12 LMS",
    "Bid-FIG / Procurement",
    "Invoice Audit",
    "AIOS Platform",
    "CRM",
    "Swan AI Labs",
    "Custom Development",
    "Other",
];

const initialState = {
    firstName: "",
    lastName: "",
    organization: "",
    email: "",
    phone: "",
    role: "",
    interest: [],
    details: "",
};

export default function Contact() {
    const content = useSiteContent();
    const [form, setForm] = useState(initialState);
    const [status, setStatus] = useState({ success: null, error: null, loading: false });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm({
            ...form,
            [name]: name === "interest" ? (typeof value === "string" ? value.split(",") : value) : value,
        });
    };

    const validate = () => {
        if (
            !form.firstName.trim() ||
            !form.lastName.trim() ||
            !form.organization.trim() ||
            !form.email.trim() ||
            !form.phone.trim() ||
            !form.details.trim()
        ) return false;
        if (!/\S+@\S+\.\S+/.test(form.email)) return false;
        if (!/\d{7,}/.test(form.phone)) return false;
        return true;
    };

    const buildHtml = () => `
    <div style="font-family:Segoe UI,Arial,sans-serif;max-width:600px;margin:0 auto;">
      <h2 style="background:#21dbb8;padding:18px 0;color:#054067;text-align:center;margin-top:0;">
        New Swan Solutions Contact Request
      </h2>
      <table style="width:100%;font-size:1rem;border-collapse:collapse;">
        <tr><td style="font-weight:bold;padding:8px;">First Name:</td><td>${form.firstName}</td></tr>
        <tr><td style="font-weight:bold;padding:8px;">Last Name:</td><td>${form.lastName}</td></tr>
        <tr><td style="font-weight:bold;padding:8px;">District/Organization:</td><td>${form.organization}</td></tr>
        <tr><td style="font-weight:bold;padding:8px;">Email:</td><td>${form.email}</td></tr>
        <tr><td style="font-weight:bold;padding:8px;">Phone:</td><td>${form.phone}</td></tr>
        ${form.role ? `<tr><td style="font-weight:bold;padding:8px;">Role/Title:</td><td>${form.role}</td></tr>` : ""}
        ${form.interest.length ? `<tr><td style="font-weight:bold;padding:8px;">Interest:</td><td>${form.interest.join(", ")}</td></tr>` : ""}
        <tr><td style="font-weight:bold;padding:8px;">Details/Message:</td><td>${form.details.replace(/\n/g, "<br/>")}</td></tr>
      </table>
      <hr style="margin:30px 0 12px 0;">
      <div style="color:#7e8b99;font-size:0.96rem;">Contact form submitted from <b>swansolutions.com</b></div>
    </div>
  `;

    const buildText = () => `
New Swan Solutions Contact Request

First Name: ${form.firstName}
Last Name: ${form.lastName}
District/Organization: ${form.organization}
Email: ${form.email}
Phone: ${form.phone}
${form.role ? `Role/Title: ${form.role}` : ""}
${form.interest.length ? `Interest: ${form.interest.join(", ")}` : ""}
Details/Message:
${form.details}
  `;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            setStatus({ ...status, error: "Please fill out all required fields correctly." });
            return;
        }
        setStatus({ loading: true, error: null, success: null });
        try {
            const res = await fetch("https://mailgun.swancreate.com/send", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    to: "dusty@swank12.com,becki@figk-12.com",
                    subject: "New Contact Request � Swan Solutions Website",
                    html: buildHtml(),
                    text: buildText(),
                }),
            });
            if (res.ok) {
                try {
                    await fetch("https://figazuresql.swancreate.com/api/crm/sk12_contactsubmissions", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            Name: `${form.firstName} ${form.lastName}`,
                            Email: form.email,
                            Phone: form.phone,
                            Interests: form.interest.length ? JSON.stringify(form.interest) : "",
                            Message: form.details,
                            Status: "New",
                        }),
                    });
                } catch (e) { /* non-fatal: email already sent */ }
                setStatus({ loading: false, success: "Thank you! Your message has been sent.", error: null });
                setForm(initialState);
            } else {
                throw new Error();
            }
        } catch {
            setStatus({ loading: false, error: "Sorry, there was a problem sending your message. Please try again.", success: null });
        }
    };

    return (
        <Box sx={{ bgcolor: "#f7fafb", minHeight: "100vh" }}>
            {/* Hero */}
            <Box
                component={motion.section}
                initial={{ opacity: 0, y: -64 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1 }}
                sx={{
                    width: "100vw",
                    left: "50%",
                    right: "50%",
                    marginLeft: "-50vw",
                    marginRight: "-50vw",
                    px: 0,
                    py: { xs: 10, md: 14 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    color: "#fff",
                    background: "linear-gradient(120deg, #0b223e 0%, #2269a6 45%, #21dbb8 100%)",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                    minHeight: { xs: "30vh", md: "36vh" },
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: "0 6px 40px 0 rgba(30,90,150,0.13)",
                    mb: 8,
                }}
            >
                <Typography
                    variant="h2"
                    sx={{
                        fontWeight: 900,
                        fontSize: { xs: "2.1rem", md: "2.8rem" },
                        color: "#fff",
                        mb: 2,
                        letterSpacing: "-0.04em",
                        textShadow: "0 3px 16px rgba(25,60,110,0.15)",
                    }}
                >
                    {content("contact.hero.title", "Contact Swan Solutions")}
                </Typography>
                <Typography
                    variant="h5"
                    sx={{
                        color: "rgba(255,255,255,0.95)",
                        fontWeight: 400,
                        maxWidth: 650,
                        mx: "auto",
                        fontSize: { xs: "1.05rem", md: "1.18rem" },
                        mb: 1,
                        textShadow: "0 2px 14px rgba(20,92,140,0.13)",
                    }}
                >
                    {content("contact.hero.subtitle", "Let us know about your project, interest, or request, and a real person will follow up within one business day.")}
                </Typography>
            </Box>

            <Container maxWidth="sm" sx={{ bgcolor: "#fff", py: 6, borderRadius: 5, boxShadow: 3, mb: 12 }}>
                <form onSubmit={handleSubmit} autoComplete="off">
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                label="First Name"
                                name="firstName"
                                value={form.firstName}
                                onChange={handleChange}
                                fullWidth
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                label="Last Name"
                                name="lastName"
                                value={form.lastName}
                                onChange={handleChange}
                                fullWidth
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required
                                label="District or Organization Name"
                                name="organization"
                                value={form.organization}
                                onChange={handleChange}
                                fullWidth
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                label="Email"
                                name="email"
                                value={form.email}
                                onChange={handleChange}
                                fullWidth
                                variant="outlined"
                                type="email"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                label="Phone"
                                name="phone"
                                value={form.phone}
                                onChange={handleChange}
                                fullWidth
                                variant="outlined"
                                type="tel"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Your Role/Title"
                                name="role"
                                value={form.role}
                                onChange={handleChange}
                                fullWidth
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel id="interest-label">Interested In</InputLabel>
                                <Select
                                    labelId="interest-label"
                                    multiple
                                    name="interest"
                                    value={form.interest}
                                    onChange={handleChange}
                                    input={<OutlinedInput label="Interested In" />}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => (
                                                <Chip key={value} label={value} sx={{ bgcolor: "#eaf5fa" }} />
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {interestOptions.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required
                                label="Details or What Services You Need"
                                name="details"
                                value={form.details}
                                onChange={handleChange}
                                fullWidth
                                variant="outlined"
                                multiline
                                minRows={5}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mt: 2 }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    component={motion.button}
                                    whileTap={{ scale: 0.96 }}
                                    whileHover={{ scale: 1.03 }}
                                    sx={{
                                        borderRadius: 99,
                                        px: 5,
                                        py: 1.3,
                                        fontWeight: 700,
                                        fontSize: "1.15rem",
                                        background: "linear-gradient(90deg, #145c8c, #21dbb8)",
                                    }}
                                    disabled={status.loading}
                                >
                                    {status.loading ? "Sending..." : "Send Message"}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
                <Box sx={{ mt: 3 }}>
                    {status.success && <Alert severity="success">{status.success}</Alert>}
                    {status.error && <Alert severity="error">{status.error}</Alert>}
                </Box>
            </Container>
        </Box>
    );
}
